import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Copy, DollarSign, FileText, Clock, CheckCircle2 } from "lucide-react";

/** Commission tiers — paid 30 days post-closing */
export function calculateSourcerCommission(askingPrice: number): number {
  if (!askingPrice || askingPrice < 250_000) return 0;
  if (askingPrice < 500_000) return 250;
  if (askingPrice < 1_000_000) return 500;
  if (askingPrice < 2_500_000) return 1_000;
  if (askingPrice < 5_000_000) return 2_000;
  return 3_500;
}

const fmtUSD = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

interface DealRow {
  id: string;
  title?: string;
  legalName?: string;
  tradeName?: string;
  askingPrice?: number;
  currentStage?: string;
  createdAt?: any;
}

export default function DealSourcerDashboard() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const lang = (language as "en" | "fr") || "en";

  const [deals, setDeals] = useState<DealRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    const q = query(
      collection(db, "dealSubmissions"),
      where("sourcedBy", "==", user.id),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDeals(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
      },
      (err) => {
        console.warn("[DealSourcer] snapshot error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.id]);

 const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteType, setInviteType] = useState<'seller' | 'broker'>('seller');
  const [sending, setSending] = useState(false);

  const baseUrl = 'https://acquizgroup.com';
  const uid = user?.id || '{uid}';

  const links = useMemo(() => ({
    seller: `${baseUrl}/register/sellers?sourcer=${uid}`,
    broker: `${baseUrl}/register/brokers?sourcer=${uid}`,
    direct: `${baseUrl}/submit-deal?ref=${uid}&role=deal_sourcer`,
  }), [uid]);

  const copyLink = async (type: 'seller' | 'broker' | 'direct') => {
    try {
      await navigator.clipboard.writeText(links[type]);
      toast({ title: lang === "fr" ? "Lien copié" : "Link copied" });
    } catch {
      toast({ title: lang === "fr" ? "Erreur de copie" : "Copy failed", variant: "destructive" });
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail || !user) return;
    setSending(true);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { app } = await import('@/lib/firebase');
      const functions = getFunctions(app, 'us-central1');
      const sendInviteEmail = httpsCallable(functions, 'sendSourcerInviteEmail');
      await sendInviteEmail({
        to: inviteEmail,
        sourcerName: user.displayName || user.email || 'A Deal Sourcer',
        sourcerEmail: user.email || '',
        inviteType,
        registrationLink: links[inviteType],
        language: lang,
      });
      toast({
        title: lang === "fr" ? "Invitation envoyée!" : "Invitation sent!",
        description: inviteEmail,
      });
      setInviteEmail('');
      setShowInviteDialog(false);
    } catch (err: any) {
      toast({
        title: lang === "fr" ? "Erreur" : "Error",
        description: err?.message || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const totals = useMemo(() => {
    const total = deals.reduce((acc, d) => acc + calculateSourcerCommission(d.askingPrice || 0), 0);
    const closed = deals.filter((d) => d.currentStage === "closing").length;
    return { totalPotential: total, totalDeals: deals.length, closed };
  }, [deals]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#1D314D]">
          {lang === "fr" ? "Tableau de bord Deal Sourcer" : "Deal Sourcer Dashboard"}
        </h1>
        <p className="text-muted-foreground">
          {lang === "fr"
            ? "Suivez vos deals soumis et vos commissions en temps réel"
            : "Track your submitted deals and commissions in real time"}
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{lang === "fr" ? "Deals soumis" : "Submitted deals"}</CardDescription>
            <CardTitle className="text-3xl text-[#1D314D] flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#CEAB65]" />
              {totals.totalDeals}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{lang === "fr" ? "Deals clôturés" : "Closed deals"}</CardDescription>
            <CardTitle className="text-3xl text-[#1D314D] flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-[#CEAB65]" />
              {totals.closed}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {lang === "fr" ? "Commission potentielle" : "Potential commission"}
            </CardDescription>
            <CardTitle className="text-3xl text-[#1D314D] flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-[#CEAB65]" />
              {fmtUSD(totals.totalPotential)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Referral links */}
      <Card className="border-[#CEAB65]/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-[#1D314D]">
                {lang === "fr" ? "Mes liens de référence" : "My Referral Links"}
              </CardTitle>
              <CardDescription>
                {lang === "fr"
                  ? "Partagez le lien approprié selon le profil de votre contact"
                  : "Share the appropriate link based on your contact's profile"}
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowInviteDialog(true)}
              className="bg-[#1D314D] text-white gap-2"
            >
              <Mail className="h-4 w-4" />
              {lang === "fr" ? "Envoyer une invitation" : "Send Invitation"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { type: 'seller' as const, labelEn: '🏢 Seller Link', labelFr: '🏢 Lien Vendeur', descEn: 'Owner sells directly', descFr: 'Propriétaire vend directement' },
            { type: 'broker' as const, labelEn: '🤝 Broker Link', labelFr: '🤝 Lien Courtier', descEn: 'A broker manages the transaction', descFr: 'Un courtier gère la transaction' },
            { type: 'direct' as const, labelEn: '⚡ Direct Link', labelFr: '⚡ Lien Direct', descEn: 'Already registered on platform', descFr: 'Déjà inscrit sur la plateforme' },
          ].map(item => (
            <div key={item.type} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#1D314D] mb-1">
                  {lang === 'fr' ? item.labelFr : item.labelEn}
                  <span className="text-muted-foreground font-normal ml-2">
                    — {lang === 'fr' ? item.descFr : item.descEn}
                  </span>
                </p>
                <Input value={links[item.type]} readOnly className="font-mono text-xs h-8" />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyLink(item.type)}
                className="shrink-0 gap-1"
              >
                <Copy className="h-3 w-3" />
                {lang === "fr" ? "Copier" : "Copy"}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#1D314D]">
              {lang === "fr" ? "Envoyer une invitation" : "Send an Invitation"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{lang === "fr" ? "Type de contact" : "Contact type"}</Label>
              <Select value={inviteType} onValueChange={(v) => setInviteType(v as 'seller' | 'broker')}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seller">
                    {lang === "fr" ? "Vendeur (propriétaire direct)" : "Seller (direct owner)"}
                  </SelectItem>
                  <SelectItem value="broker">
                    {lang === "fr" ? "Courtier (broker)" : "Broker"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{lang === "fr" ? "Courriel du destinataire" : "Recipient email"}</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              {lang === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              onClick={sendInvite}
              disabled={sending || !inviteEmail}
              className="bg-[#CEAB65] text-[#1D314D] font-semibold"
            >
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {lang === "fr" ? "Envoyer" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1D314D]">
            {lang === "fr" ? "Barème de commissions" : "Commission schedule"}
          </CardTitle>
          <CardDescription>
            {lang === "fr" ? "Payée 30 jours après le closing" : "Paid 30 days after closing"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-5 text-sm">
            {[
              { range: "$250K – $500K", payout: "$250" },
              { range: "$500K – $1M", payout: "$500" },
              { range: "$1M – $2.5M", payout: "$1,000" },
              { range: "$2.5M – $5M", payout: "$2,000" },
              { range: "$5M+", payout: "$3,500" },
            ].map((t) => (
              <div key={t.range} className="rounded-lg border p-3 text-center">
                <div className="text-xs text-muted-foreground">{t.range}</div>
                <div className="text-lg font-semibold text-[#CEAB65]">{t.payout}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Deals pipeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#1D314D]">
            {lang === "fr" ? "Mes deals soumis" : "My submitted deals"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</p>
          ) : deals.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {lang === "fr"
                ? "Aucun deal soumis pour l'instant. Partagez votre lien !"
                : "No deals submitted yet. Share your link!"}
            </p>
          ) : (
            <div className="space-y-2">
              {deals.map((d) => {
                const commission = calculateSourcerCommission(d.askingPrice || 0);
                const name = d.tradeName || d.legalName || d.title || d.id;
                return (
                  <div
                    key={d.id}
                    className="flex items-center justify-between border rounded-lg p-3 hover:bg-muted/40"
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-[#CEAB65]" />
                      <div>
                        <div className="font-medium text-[#1D314D]">{name}</div>
                        <div className="text-xs text-muted-foreground">
                          {d.askingPrice ? fmtUSD(d.askingPrice) : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="border-[#1D314D]/30 text-[#1D314D]">
                        {d.currentStage || "submission"}
                      </Badge>
                      <span className="text-sm font-semibold text-[#CEAB65]">
                        {commission ? fmtUSD(commission) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
